import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import "./buzz.css";

// TODO (όταν έρθουν Turnstile keys): φόρτωση https://challenges.cloudflare.com/turnstile/v0/api.js
// με REACT_APP_TURNSTILE_SITE_KEY και αποστολή turnstileToken στο signup body.

export default function BuzzSignup() {
  const { t, i18n } = useTranslation();
  // form → (gname: Google χωρίς ΑΦΜ) → verify (email flow) → company («Στοιχεία επιχείρησης»)
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ afm: "", email: "", password: "", password2: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [google, setGoogle] = useState(null); // {credential, email, name}
  const [afmCheck, setAfmCheck] = useState(null); // null | {status:'invalid'|'taken'|'ok'}
  const [profile, setProfile] = useState(null); // στοιχεία εταιρείας (step company)
  const [brand, setBrand] = useState("");
  const [fixAfm, setFixAfm] = useState(null); // null=κλειστό, ""=ανοιχτό πεδίο διόρθωσης
  const googleBtnRef = useRef(null);
  const lang = i18n.language?.startsWith("el") ? "el" : "en";

  // Το GIS callback αρχικοποιείται μία φορά — παίρνει τις τρέχουσες τιμές μέσω ref.
  const formRef = useRef(form);
  formRef.current = form;

  // Προέλεγχος ΑΦΜ μόλις συμπληρωθούν 9 ψηφία: εγκυρότητα + μοναδικότητα ΜΟΝΟ
  // (το μητρώο ΑΑΔΕ καλείται αποκλειστικά μετά τη δημιουργία λογαριασμού).
  const checkAfm = useCallback(async (value) => {
    if (value.length !== 9) { setAfmCheck(null); return; }
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/public/buzz/afm`, { afm: value });
      if (!data.valid) return setAfmCheck({ status: "invalid" });
      setAfmCheck({ status: data.available ? "ok" : "taken" });
    } catch {
      // Δίκτυο/500: μην μπλοκάρεις την εγγραφή — τα πεδία εμφανίζονται και ο
      // οριστικός έλεγχος του ΑΦΜ γίνεται ούτως ή άλλως στο submit.
      setAfmCheck({ status: "ok", unverified: true });
    }
  }, []);

  // Τα πεδία εγγραφής αποκαλύπτονται μόνο με έγκυρο & διαθέσιμο ΑΦΜ
  const revealed = afmCheck?.status === "ok";

  // Φόρτωση προφίλ εταιρείας όταν φτάνουμε στο βήμα «Στοιχεία επιχείρησης»
  useEffect(() => {
    if (step !== "company") return;
    (async () => {
      try {
        const { data: csrf } = await axios.get(`${config.API_BASE_URL}/csrf-token`);
        axios.defaults.headers.common["X-CSRF-Token"] = csrf.csrfToken;
        const { data } = await axios.get(`${config.API_BASE_URL}/buzz/company-profile`);
        setProfile(data.profile);
        setBrand(data.profile.brandName || data.profile.legalName || "");
      } catch { setError(t("Something went wrong — please try again.")); }
    })();
  }, [step, t]);

  const handleGoogleResult = useCallback(async (credential, afm) => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/public/buzz/google`, {
        credential, afm: afm || undefined, lang,
      });
      if (data.needBusinessName) {
        setGoogle({ credential, email: data.email, name: data.name });
        setStep("gname"); // Google χωρίς συμπληρωμένο ΑΦΜ → το ζητάμε
      } else if (data.created) {
        setStep("company"); // νέος λογαριασμός → στοιχεία επιχείρησης → billing
      } else {
        window.location.href = "/buzz/board";   // υπάρχων → κατευθείαν μέσα
      }
    } catch (e) {
      const code = e?.response?.data?.error;
      setError(
        code === "ambiguous-email" ? t("This email is used by multiple accounts — please log in with your password.") :
        code === "invalid-google-token" ? t("Google sign-in failed — please try again.") :
        code === "invalid-afm" ? t("Please enter a valid VAT number (AFM).") :
        code === "afm-in-use" ? t("This VAT number is already registered — contact us if this is your business.") :
        t("Something went wrong — please try again.")
      );
    } finally { setBusy(false); }
  }, [lang, t]);

  // Google Identity Services: το client id φορτώνεται στο mount, αλλά το κουμπί
  // ρεντάρεται μόνο όταν αποκαλυφθούν τα πεδία (revealed) — το div-στόχος δεν
  // υπάρχει στο DOM νωρίτερα.
  const [googleClientId, setGoogleClientId] = useState(null);
  const [googleReady, setGoogleReady] = useState(false); // το κουμπί όντως ρενταρίστηκε
  useEffect(() => {
    let cancelled = false;
    axios.get(`${config.API_BASE_URL}/public/buzz/auth-config`)
      .then(({ data }) => { if (!cancelled && data.googleClientId) setGoogleClientId(data.googleClientId); })
      .catch(() => { /* χωρίς Google — το κουμπί απλώς δεν εμφανίζεται */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!revealed || !googleClientId) return;
    let cancelled = false;
    const init = () => {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (resp) => handleGoogleResult(resp.credential, formRef.current.afm.trim() || undefined),
      });
      // Ίδιο στυλ με το κουμπί Google του login: pill + πλάτος όσο η φόρμα
      const w = Math.min(400, Math.max(220, googleBtnRef.current.parentElement?.offsetWidth
        || googleBtnRef.current.offsetWidth || 320));
      googleBtnRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline", size: "large", width: w, shape: "pill",
        text: "continue_with", locale: lang === "el" ? "el" : "en",
        logo_alignment: "center",
      });
      setGoogleReady(true); // μόνο τώρα δείχνουμε τα «Εγγραφή μέσω Google»/«ή με email»
    };
    if (window.google?.accounts?.id) init();
    else {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.onload = init;
      document.head.appendChild(s);
    }
    return () => { cancelled = true; };
  }, [revealed, googleClientId, handleGoogleResult, lang]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/public/buzz/signup`, {
        ...form,
        lang: i18n.language?.startsWith("el") ? "el" : "en",
      });
      // φρέσκο CSRF για τα επόμενα session calls
      const { data } = await axios.get(`${config.API_BASE_URL}/csrf-token`);
      axios.defaults.headers.common["X-CSRF-Token"] = data.csrfToken;
      setStep("verify");
    } catch (err) {
      const code = err?.response?.data?.error;
      setError(
        code === "invalid-email" ? t("Please enter a valid email.") :
        code === "weak-password" ? t("Password must be at least 8 characters.") :
        code === "invalid-business-name" ? t("Please enter your business name.") :
        code === "invalid-afm" ? t("Please enter a valid VAT number (AFM).") :
        code === "afm-in-use" ? t("This VAT number is already registered — contact us if this is your business.") :
        t("Something went wrong — please try again.")
      );
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/verify-email`, { code });
      setError(null); setStep("company"); // επόμενο βήμα: στοιχεία επιχείρησης → billing
    } catch {
      setError(t("Wrong or expired code."));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    await axios.post(`${config.API_BASE_URL}/buzz/verify-email/resend`);
  };

  // «Λάθος ΑΦΜ» → authenticated re-lookup & ενημέρωση εταιρείας
  const updateAfm = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/buzz/company-profile`, { afm: fixAfm });
      setProfile(data.profile);
      setBrand(data.profile.brandName || data.profile.legalName || "");
      setFixAfm(null);
    } catch (e) {
      const code = e?.response?.data?.error;
      const left = e?.response?.data?.attemptsLeft;
      if (code === "afm-locked") {
        setProfile((p) => ({ ...p, afmLocked: true }));
        setError(null);
        return;
      }
      const leftNote = Number.isInteger(left) ? ` (${t("attempts left")}: ${left})` : "";
      setError((
        code === "invalid-afm" ? t("Please enter a valid VAT number (AFM).") :
        code === "afm-in-use" ? t("This VAT number is already registered — contact us if this is your business.") :
        t("Something went wrong — please try again.")
      ) + leftNote);
      if (Number.isInteger(left)) setProfile((p) => ({ ...p, attemptsLeft: left }));
    } finally { setBusy(false); }
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/company-profile`, { brandName: brand });
      window.location.href = "/buzz/billing"; // τελευταίο βήμα wizard: κάρτα → trial
    } catch {
      setError(t("Something went wrong — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <div className="bz-signup">
      {step === "form" ? (
        <form onSubmit={submit}>
          <h1><span className="bzTitleBell" aria-hidden="true">🔔</span> EstiaSync <span className="bzBrandOrange">Buzz</span></h1>
          <div className="sub">{t("Waiter call with a QR — set up in 5 minutes. 15-day free trial.")}</div>

          {/* ΑΦΜ ΠΡΙΝ τις επιλογές εγγραφής — ισχύει και για τα δύο flows. Μόνο
              εγκυρότητα/μοναδικότητα εδώ· τα στοιχεία μητρώου έρχονται στο επόμενο βήμα. */}
          <input type="text" required inputMode="numeric" maxLength={9} placeholder={t("VAT number (AFM)")}
            className="bzAfmInput" value={form.afm}
            onChange={e => {
              const afm = e.target.value.replace(/\D/g, "").slice(0, 9);
              setForm({ ...form, afm });
              checkAfm(afm);
            }} />
          <div className="bz-hint" style={{ fontSize: 12, textAlign: "left", margin: "2px 2px 0" }}>
            {t("The ΑΦΜ of your business (not your personal one) — used to verify the company and for invoicing.")}
          </div>
          {afmCheck?.status === "invalid" && <div className="bz-banner err">{t("Please enter a valid VAT number (AFM).")}</div>}
          {afmCheck?.status === "taken" && <div className="bz-banner err">{t("This VAT number is already registered — contact us if this is your business.")}</div>}

          {revealed && (
            <div className="bzReveal">
              {/* Χωρίς φορτωμένο Google (adblock/χωρίς client id) οι δύο διαχωριστικές
                  γραμμές δεν έχουν νόημα — ο χρήστης βλέπει κατευθείαν τη φόρμα email.
                  Το div-στόχος μένει πάντα mounted (εκεί ρεντάρει η GIS το κουμπί). */}
              {googleReady && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0", color: "#9ca3af", fontSize: 13 }}>
                  <span style={{ flex: 1, borderTop: "1px solid #e5e7eb" }} />
                  {t("Register via Google")}
                  <span style={{ flex: 1, borderTop: "1px solid #e5e7eb" }} />
                </div>
              )}
              <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", margin: "0 0 6px" }} />
              {googleReady && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0", color: "#9ca3af", fontSize: 13 }}>
                  <span style={{ flex: 1, borderTop: "1px solid #e5e7eb" }} />
                  {t("or with email")}
                  <span style={{ flex: 1, borderTop: "1px solid #e5e7eb" }} />
                </div>
              )}

              <input type="email" required maxLength={45} placeholder="Email"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <input type="password" required minLength={8} placeholder={t("Password (min. 8 characters)")}
                value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              <input type="password" required minLength={8} placeholder={t("Confirm password")}
                value={form.password2} onChange={e => setForm({ ...form, password2: e.target.value })} />
              <div className="bz-hint" style={{ fontSize: 12, textAlign: "left", margin: "2px 2px 6px" }}>
                {t("At least 8 characters — a phrase of 2-3 words works great.")}
              </div>
              {form.password2 && form.password !== form.password2 && (
                <div className="bz-banner err">{t("Passwords do not match.")}</div>
              )}
              {error && <div className="bz-banner err">{error}</div>}
              <button className="bz-btn primary" type="submit"
                disabled={busy
                  || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
                  || form.password.length < 8
                  || form.password !== form.password2}>
                {t("Create my account")}
              </button>
              <div className="bz-hint" style={{ fontSize: 12, textAlign: "center", marginTop: 10 }}>
                {t("By creating an account (via Google or email) you accept the")}{" "}
                <a href={`${config.API_BASE_URL}/public/legal/terms`} target="_blank" rel="noreferrer" style={{ color: "#d97706" }}>{t("Terms of Service")}</a>{" "}
                {t("and the")}{" "}
                <a href={`${config.API_BASE_URL}/public/legal/contract`} target="_blank" rel="noreferrer" style={{ color: "#d97706" }}>{t("Service Agreement & DPA")}</a>.
              </div>
            </div>
          )}
        </form>
      ) : step === "gname" ? (
        <form onSubmit={(e) => { e.preventDefault(); handleGoogleResult(google.credential, form.afm); }}>
          <h1>{t("Almost there!")} 👋</h1>
          <div className="sub">
            {google?.name} ({google?.email}) — {t("just tell us your VAT number (AFM):")}
          </div>
          <input type="text" required inputMode="numeric" maxLength={9} autoFocus placeholder={t("VAT number (AFM)")}
            value={form.afm}
            onChange={e => {
              const afm = e.target.value.replace(/\D/g, "").slice(0, 9);
              setForm({ ...form, afm });
              checkAfm(afm);
            }} />
          {afmCheck?.status === "invalid" && <div className="bz-banner err">{t("Please enter a valid VAT number (AFM).")}</div>}
          {afmCheck?.status === "taken" && <div className="bz-banner err">{t("This VAT number is already registered — contact us if this is your business.")}</div>}
          {error && <div className="bz-banner err">{error}</div>}
          <button className="bz-btn primary" disabled={busy || form.afm.length !== 9} type="submit">
            {t("Create my account")}
          </button>
          <div className="bz-hint" style={{ fontSize: 12, textAlign: "center", marginTop: 10 }}>
            {t("By creating an account (via Google or email) you accept the")}{" "}
            <a href={`${config.API_BASE_URL}/public/legal/terms`} target="_blank" rel="noreferrer" style={{ color: "#d97706" }}>{t("Terms of Service")}</a>{" "}
            {t("and the")}{" "}
            <a href={`${config.API_BASE_URL}/public/legal/contract`} target="_blank" rel="noreferrer" style={{ color: "#d97706" }}>{t("Service Agreement & DPA")}</a>.
          </div>
        </form>
      ) : step === "company" ? (
        <form onSubmit={saveCompany}>
          <h1>{t("Business details")} 🏪</h1>
          {!profile ? (
            <div className="sub">…</div>
          ) : profile.afmLocked ? (
            <div className="bz-banner err" style={{ textAlign: "left" }}>
              🔒 {t("Your account was locked after too many VAT number attempts. Contact us at info@estiasync.com to verify your business and unlock it.")}
            </div>
          ) : (
            <>
              <div className="bz-banner" style={{ background: "#f0f9ff", borderColor: "#bae6fd", color: "#0c4a6e", textAlign: "left" }}>
                <div style={{ fontSize: 12, color: "#0369a1", marginBottom: 4 }}>{t("From the AADE registry (used on invoices):")}</div>
                <div><b>{profile.legalName}</b></div>
                {profile.address && <div style={{ fontSize: 13 }}>{profile.address}</div>}
                <div style={{ fontSize: 13 }}>{t("VAT number (AFM)")}: {profile.taxid}</div>
                {fixAfm === null ? (
                  profile.attemptsLeft > 0 && (
                    <button type="button" className="bz-btn ghost" style={{ marginTop: 8, fontSize: 13 }}
                      onClick={() => setFixAfm("")}>
                      {t("Wrong VAT number? Fix it")}
                    </button>
                  )
                ) : (
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <input type="text" inputMode="numeric" maxLength={9} autoFocus placeholder={t("VAT number (AFM)")}
                      value={fixAfm} style={{ flex: 1 }}
                      onChange={e => setFixAfm(e.target.value.replace(/\D/g, "").slice(0, 9))} />
                    <button type="button" className="bz-btn" disabled={busy || fixAfm.length !== 9} onClick={updateAfm}>
                      {t("Update")}
                    </button>
                  </div>
                )}
              </div>

              <label style={{ display: "block", textAlign: "left", fontSize: 13, fontWeight: 600, margin: "12px 0 4px" }}>
                {t("Trade name — this is what your customers see")}
              </label>
              <input type="text" required maxLength={45} value={brand}
                onChange={e => setBrand(e.target.value)} />
              <div className="sub" style={{ fontSize: 12, marginTop: 4 }}>
                {t("Shown on the QR page and messages to guests. You can change it anytime in Settings.")}
              </div>

              {error && <div className="bz-banner err">{error}</div>}
              <button className="bz-btn primary" disabled={busy || brand.trim().length < 2} type="submit">
                {t("Continue")} →
              </button>
            </>
          )}
        </form>
      ) : (
        <form onSubmit={verify}>
          <h1>{t("Check your inbox")} 📬</h1>
          <div className="sub">
            {t("We sent a 6-digit code to")} <b>{form.email}</b>.{" "}
            {t("It usually arrives within the next 1-2 minutes — check your spam folder too.")}
          </div>
          <input className="bz-code-input" type="text" inputMode="numeric" pattern="\d{6}"
            maxLength={6} required value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} />
          {error && <div className="bz-banner err">{error}</div>}
          <button className="bz-btn primary" disabled={busy || code.length !== 6} type="submit">
            {t("Verify")}
          </button>
          <button className="bz-btn ghost" type="button" onClick={resend} style={{ marginTop: 8 }}>
            {t("Resend code")}
          </button>
        </form>
      )}
    </div>
  );
}
